import React, { useEffect, useRef, useState } from "react";

import { fetchAdminTasks } from "../../../api/adminData";
import {
  TASK_STATUS_BADGE,
  TASK_STATUS_BADGE_FALLBACK,
  TASK_STATUS_LABEL,
  taskRowDateLabel,
} from "../tasks/EntityTasksTab";

/**
 * The first few tasks linked to this tenancy, on the Übersicht.
 *
 * NOT A SECOND TASK LIST. It reads the same endpoint with the same
 * `tenancy_id` filter the Aufgaben tab uses, and it prints statuses and dates
 * through `EntityTasksTab`'s own maps and date rule — so a task cannot read
 * "In Arbeit" here and something else one tab over. It deliberately offers no
 * create, no edit and no row action: that is the Aufgaben tab's job, one
 * click away, and a second half-implementation of it is exactly what this
 * preview must not become.
 *
 * THE ORDER IS THE SERVER'S. `sort="picker_relevance"` is the existing
 * ordering for "what still needs doing": unfinished work first, then done,
 * then cancelled, soonest deadline first inside each bucket — so an overdue
 * task surfaces on its own. No client-side ranking is invented here.
 *
 * ONE SMALL REQUEST. `limit=3` asks for exactly what is shown; it is not a
 * duplicate of the tab's full list but a different, cheaper question, and it
 * costs one request per selected tenancy — never one per row.
 */

const PREVIEW_LIMIT = 3;

export default function TenancyTasksPreview({ tenancyId }) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    items: [],
    total: 0,
  });
  // Guards a response for a previously selected row landing after a newer one.
  const requested = useRef(null);

  useEffect(() => {
    requested.current = tenancyId;
    if (!tenancyId) {
      setState({ loading: false, error: null, items: [], total: 0 });
      return;
    }
    setState({ loading: true, error: null, items: [], total: 0 });
    fetchAdminTasks({
      tenancy_id: tenancyId,
      sort: "picker_relevance",
      limit: PREVIEW_LIMIT,
    })
      .then((d) => {
        if (requested.current !== tenancyId) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        setState({
          loading: false,
          error: null,
          items,
          total: d?.total ?? items.length,
        });
      })
      .catch((e) => {
        if (requested.current !== tenancyId) return;
        setState({
          loading: false,
          error: e?.message || "Aufgaben konnten nicht geladen werden.",
          items: [],
          total: 0,
        });
      });
  }, [tenancyId]);

  if (state.loading) {
    return (
      <div
        className="flex flex-col gap-2"
        aria-busy="true"
        data-testid="tenancy-tasks-preview-loading"
      >
        <span className="sr-only">Aufgaben werden geladen …</span>
        {[0, 1].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            className="h-[34px] animate-pulse rounded-[7px] border border-border bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (state.error) {
    // Local and quiet: a failing preview must not take the Übersicht with it.
    return (
      <p
        role="alert"
        data-testid="tenancy-tasks-preview-error"
        className="m-0 text-[12.5px] text-rose-700 dark:text-rose-300"
      >
        {state.error}
      </p>
    );
  }

  if (state.items.length === 0) {
    return (
      <p
        className="m-0 text-[12.5px] text-muted-foreground"
        data-testid="tenancy-tasks-preview-empty"
      >
        Keine Aufgaben mit diesem Mietverhältnis verknüpft.
      </p>
    );
  }

  const hidden = state.total - state.items.length;

  return (
    <div data-testid="tenancy-tasks-preview">
      <ul className="m-0 flex list-none flex-col gap-[6px] p-0">
        {state.items.map((task) => (
          <li
            key={task.id}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-[7px] border border-border bg-background px-[10px] py-[6px]"
          >
            <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
              {task.title}
            </span>
            {/* The status is a word, not only a colour. */}
            <span
              className={`inline-flex shrink-0 items-center rounded-full border px-2 py-[1px] text-[10px] font-semibold ${
                TASK_STATUS_BADGE[task.status] || TASK_STATUS_BADGE_FALLBACK
              }`}
            >
              {TASK_STATUS_LABEL[task.status] || task.status}
            </span>
            <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">
              {taskRowDateLabel(task)}
            </span>
          </li>
        ))}
      </ul>
      {hidden > 0 ? (
        <p className="m-0 mt-2 text-[11.5px] text-muted-foreground">
          {hidden === 1
            ? "1 weitere Aufgabe"
            : `${hidden} weitere Aufgaben`}{" "}
          im Tab „Aufgaben“.
        </p>
      ) : null}
    </div>
  );
}
